import { TriangleAlert } from 'lucide-react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { CurrentUserI } from 'api/users/interfaces/Users';

import { Button } from 'components/ui/button';

import { ArcaContext } from 'context/ArcaContext.context';
import { I18nContext } from 'context/translations/TranslationsContext.context';

import { useContactDataBanner, CONTACT_DATA_BANNER_EVENTS } from './useContactDataBanner';

const SETTINGS_ROUTE_WITH_FOCUS = '/dashboard/settings?focus=personal-data';

interface ContactDataBannerProps {
	user: CurrentUserI['user'] | null | undefined;
}

const ContactDataBanner = ({ user }: ContactDataBannerProps) => {
	const navigate = useNavigate();
	const { token } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { isVisible, onCtaClick } = useContactDataBanner(user, token);

	if (!isVisible) return null;

	const handleClick = () => {
		onCtaClick();
		navigate(SETTINGS_ROUTE_WITH_FOCUS);
	};

	return (
		/*
		 * 🔴 `mt-auto` + `sticky`, no `fixed`.
		 *
		 * El contenedor de `Dashboard.tsx` es `flex flex-col min-h-screen` y el contenido va en un
		 * envoltorio con `grow`, así que el banner queda empujado al fondo aunque el contenido sea
		 * un spinner — que es cuando antes aparecía a media altura. `mt-auto` se queda como
		 * segunda red: si alguien quita el `grow`, el banner sigue abajo. Y `sticky` lo mantiene
		 * abajo mientras se desplaza una página larga.
		 *
		 * `fixed` no vale: queda anclado a la ventana y se come el ancho completo, tapando la ficha
		 * de usuario del panel lateral.
		 *
		 * `bottom-16` en móvil porque ahí vive la barra de navegación, y `md:bottom-0` desde tablet,
		 * donde no la hay.
		 *
		 * ⚠️ El `pb-[env(safe-area-inset-bottom)]` NO hace lo mismo en los dos casos, aunque lo
		 * parezca:
		 *
		 *   · desde `md` no hay barra, el banner llega al borde de la ventana y el relleno reserva
		 *     el área segura del indicador de inicio — que es lo que pide la regla
		 *     `safe-area-aware` del DSDS;
		 *   · en móvil esa área ya la cubre la barra. Lo que compensa el relleno aquí es OTRA cosa:
		 *     `MobileBottomNav` (Proa) mide `h-16` MÁS su propio inset de área segura, o sea más de los
		 *     64 px que libera `bottom-16`, así que la barra solapa el fondo del banner justo ese
		 *     inset. Como lo solapado es relleno vacío, el texto no se tapa.
		 *
		 * O sea que en móvil funciona por compensación, no por diseño: si la barra dejara de crecer
		 * con el área segura, este relleno pasaría a sobrar y el banner quedaría separado de ella.
		 * Sin medir en un iPhone de verdad.
		 */
		<div
			data-testid="contact-data-banner"
			role="region"
			aria-label={translate('banner.contactData.ariaLabel')}
			className="sticky bottom-16 z-40 mt-auto -mx-4 border-t border-border bg-card pb-[env(safe-area-inset-bottom,0px)] md:bottom-0"
		>
			{/* Fondo BLANCO y borde neutro, no una superficie de color. El aviso lo da el icono
			    ámbar y el texto; teñir toda la banda lo hacía parecer un error.

			    Antes eran `#feebf0`, `#f83566`, `#1b3226` y `#fa3f6e` escritos a mano: un rosa que ni
			    seguía a la paleta ni podía seguirla.

			    ⚠️ Y NO se usa `bg-warning-surface`, aunque el nombre invite: esa variable solo está
			    declarada dentro de `.dark` en `styles/tokens.css`, igual que `--info-surface` y
			    `--destructive-surface`. En claro no existe, así que la utilidad no pinta nada y el
			    banner salía TRANSPARENTE. Al sistema le faltan las superficies claras — mismo patrón
			    que ya dio problemas con `--accent-surface` en el chat. */}
			<div className="flex w-full flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:gap-4 md:px-12 md:py-3">
				{/*
				 * El icono va junto al título en móvil y a la izquierda de todo desde `md`, sin
				 * duplicarlo: esta envoltura los agrupa en una fila propia cuando la banda está
				 * apilada, y `md:contents` la hace desaparecer de la maqueta, dejando al icono y al
				 * texto como hijos directos de la fila. Sin ella, el icono ocuparía una línea entera
				 * él solo encima del título, porque en móvil la banda es `flex-col`.
				 */}
				<div className="flex items-start gap-3 md:contents">
					{/*
					 * `--warning-text`, no `--warning`, y por eso se esperó a esta sincronización.
					 *
					 * `--warning` vale `amber-84` en claro y `amber-83` en oscuro: casi el mismo
					 * amarillo claro en los dos temas, porque un solo token hacía de acento sobre
					 * fondo oscuro Y de primer plano sobre fondo claro, que son dos papeles con
					 * necesidades opuestas. Sobre este banner blanco daba 1,64:1 y el icono se veía
					 * lavado.
					 *
					 * Proa lo partió por papel en la v0.6.0. En oscuro no cambia nada —allí
					 * `--warning-text` vale exactamente `--warning`—, y en claro sube a 7:1.
					 */}
					<TriangleAlert
						className="size-6 shrink-0 text-warning-text md:size-9"
						strokeWidth={2}
						aria-hidden="true"
					/>
					<div className="flex min-w-0 flex-1 flex-col">
						{/* 🔴 Sin `font-['Inter']` ni tamaños en píxeles: la tipografía sale del sistema.
						    El botón llevaba `font-['Roboto']` SIN alternativa, así que al dejar de
						    servirse Roboto el navegador cayó a su fuente por defecto y salía en SERIF. */}
						<p className="font-medium md:text-lg">{translate('banner.contactData.title')}</p>
						<p className="text-sm text-muted-foreground md:text-base">
							{translate('banner.contactData.description')}
						</p>
					</div>
				</div>
				<Button
					data-attr={CONTACT_DATA_BANNER_EVENTS.CLICKED}
					onClick={handleClick}
					className="shrink-0 self-end md:self-auto"
				>
					{translate('banner.contactData.cta')}
				</Button>
			</div>
		</div>
	);
};

export default ContactDataBanner;
